"use client";

import { FC, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SearchMenu } from "./search-menu";
import { Search } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { cn } from "@erp/shared-libs/utils";
import { Selectable } from "@erp/shared-libs/common";

interface SearchInputFormProps {
  className?: string;
  options: Selectable[];
  /**
   * 外觀
   * - default：藥丸搜尋框（既有樣式）
   * - ai：扁平 pill 搜尋框（對應設計稿，圓角矩形 + 主色 focus 框）
   */
  variant?: "default" | "ai";
  onSubmit: (obj: Record<string, string>) => void;
}

/**
 * 關鍵字的比對方式：包含，或不包含
 */
type SearchMatchMode = "contains" | "notContains";

export const SearchInputForm: FC<SearchInputFormProps> = ({
  className,
  options,
  variant = "default",
  onSubmit,
}) => {
  // =================================================
  // Hooks
  // =================================================
  const { t } = useTranslation("common");

  /**
   * 目前選的搜尋欄位
   */
  const [searchOptionId, setSearchOptionId] = useState(options[0].id as string);

  /**
   * 目前選的比對方式；送出後留在原位，方便連續加同一種條件
   */
  const [matchMode, setMatchMode] = useState<SearchMatchMode>("contains");

  // =================================================
  // Refs
  // =================================================
  const searchFieldRef = useRef<HTMLInputElement>(null);

  // =================================================
  // Variables
  // =================================================
  /**
   * 是否為 ai pill 外觀（圓角矩形 + 主色 focus 框）
   */
  const isAi = variant === "ai";

  /**
   * 目前選的搜尋欄位
   */
  const searchOption = options.find((x) => x.id === searchOptionId);

  /**
   * 這個欄位排除時要寫入的欄位 id；沒有設定代表這個欄位只能「包含」。
   * 「包含／不包含」選單不開放，固定當成沒有設定（選單不出現、一律寫進包含的欄位）；原本的取值保留在下面的註解
   */
  // const excludedId = searchOption?.excludedId as string | undefined;
  const excludedId = undefined as string | undefined;

  /**
   * 這次送出是不是「不包含」：欄位可以排除、而且選了不包含
   */
  const isExcluding = excludedId != null && matchMode === "notContains";

  /**
   * 比對方式的選項
   */
  const matchModeOptions = useMemo(
    () => [
      { id: "contains", label: t("search_input.mode_contains"), isSelectable: true },
      { id: "notContains", label: t("search_input.mode_not_contains"), isSelectable: true },
    ],
    [t]
  );

  // =================================================
  // Functions
  // =================================================
  /**
   * 送出：把關鍵字寫進目前的欄位（選了不包含時寫進該欄位的排除欄位），輸入框清空
   */
  const handleSearch = useCallback(() => {
    if (!searchFieldRef.current) return;

    const searchValue = searchFieldRef.current.value.trim();

    if (!searchValue) return;

    // 轉換成預覽物件
    onSubmit({
      [isExcluding ? excludedId : searchOptionId]: searchValue,
    });

    searchFieldRef.current.value = "";
  }, [onSubmit, searchOptionId, excludedId, isExcluding]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !e.nativeEvent.isComposing) {
        e.preventDefault();
        handleSearch();
      }
    },
    [handleSearch]
  );

  // =================================================
  // Render
  // =================================================
  return (
    <div
      className={cn(
        "border-border bg-on-background w-114.5 flex items-center gap-1 border",
        isAi ? "border-border-lighter rounded-xl transition-colors" : "rounded-full",
        className
      )}
    >
      {/* Drop Down Menu */}
      <SearchMenu
        searchOptions={options.filter((x) => x.isSelectable)}
        searchOptionId={searchOptionId}
        variant={variant}
        onSearchOptionIdChange={setSearchOptionId}
      />

      {/* 比對方式：目前的欄位可以排除時才出現，樣式和欄位選單相同 */}
      {excludedId != null ? (
        <SearchMenu
          searchOptions={matchModeOptions}
          searchOptionId={matchMode}
          variant={variant}
          position="inner"
          ariaLabel={t("search_input.mode_label")}
          onSearchOptionIdChange={(id) => setMatchMode(id as SearchMatchMode)}
        />
      ) : null}
      <div className="flex flex-1 gap-1">
        {/* Search Input */}
        <Input
          ref={searchFieldRef}
          type="text"
          placeholder={searchOption?.placeholder}
          extra="flex-1 relative flex flex-col"
          className="mt-0 flex-1 rounded-none border-none"
          inputClassName={cn(
            isAi && "text-dense md:text-dense placeholder:text-dense md:placeholder:text-dense"
          )}
          onKeyDown={handleKeyDown}
        />
        <Button
          type="button"
          variant="outline"
          className={cn(
            "rounded-l-none border-none",
            isAi ? "text-muted-foreground hover:text-primary rounded-r-xl" : "rounded-r-full"
          )}
          onClick={handleSearch}
        >
          <Search />
        </Button>
      </div>
    </div>
  );
};
